/**
 * Inventory Repository
 * COMPLETE implementation for Inventory Items, Stock Transactions, Requisitions
 */

import { BaseRepository } from '../BaseRepository';
import { db } from '../db';
import type { InventoryItem, StockTransaction, Requisition } from '../../../types/database';

export class InventoryItemRepository extends BaseRepository<InventoryItem> {
  constructor() {
    super(db.inventoryItems, 'inventoryItems');
  }

  async getByOrganization(organizationId: string): Promise<InventoryItem[]> {
    return await this.table.where('organizationId').equals(organizationId).toArray();
  }

  async findByCategory(category: string): Promise<InventoryItem[]> {
    return await this.table.where('category').equals(category).toArray();
  }

  async findByLocation(location: string): Promise<InventoryItem[]> {
    return await this.table.where('location').equals(location).toArray();
  }

  async findLowStock(): Promise<InventoryItem[]> {
    return await this.table.filter(item =>
      item.currentStock <= item.reorderPoint
    ).toArray();
  }

  async findOutOfStock(): Promise<InventoryItem[]> {
    return await this.table.filter(item =>
      item.currentStock <= 0
    ).toArray();
  }

  async search(searchTerm: string): Promise<InventoryItem[]> {
    const term = searchTerm.toLowerCase();
    return await this.table.filter(item =>
      item.partNumber.toLowerCase().includes(term) ||
      item.partName.toLowerCase().includes(term) ||
      (item.description !== undefined && item.description.toLowerCase().includes(term)) ||
      (item.manufacturer !== undefined && item.manufacturer.toLowerCase().includes(term))
    ).toArray();
  }

  async findByPartNumber(partNumber: string): Promise<InventoryItem | undefined> {
    return await this.table.where('partNumber').equals(partNumber).first();
  }
}

export class StockTransactionRepository extends BaseRepository<StockTransaction> {
  constructor() {
    super(db.stockTransactions, 'stockTransactions');
  }

  async findByItem(itemId: string): Promise<StockTransaction[]> {
    return await this.table.where('itemId').equals(itemId).toArray();
  }

  async findByType(type: StockTransaction['transactionType']): Promise<StockTransaction[]> {
    return await this.table.where('transactionType').equals(type).toArray();
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<StockTransaction[]> {
    return await this.table
      .where('transactionDate')
      .between(startDate, endDate, true, true)
      .toArray();
  }

  async getLatestForItem(itemId: string, limit: number = 10): Promise<StockTransaction[]> {
    const transactions = await this.findByItem(itemId);
    return transactions
      .sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime())
      .slice(0, limit);
  }
}

export class RequisitionRepository extends BaseRepository<Requisition> {
  constructor() {
    super(db.requisitions, 'requisitions');
  }

  async findByStatus(status: Requisition['status']): Promise<Requisition[]> {
    return await this.table.where('status').equals(status).toArray();
  }

  async findByDepartment(department: string): Promise<Requisition[]> {
    return await this.table.where('department').equals(department).toArray();
  }

  async findPending(): Promise<Requisition[]> {
    return await this.table
      .where('status')
      .anyOf(['draft', 'submitted', 'approved'])
      .toArray();
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<Requisition[]> {
    return await this.table
      .where('requestedDate')
      .between(startDate, endDate, true, true)
      .toArray();
  }

  async findUrgent(): Promise<Requisition[]> {
    return await this.table.filter(req =>
      req.items.some(item => item.urgent)
    ).toArray();
  }

  async search(searchTerm: string): Promise<Requisition[]> {
    const term = searchTerm.toLowerCase();
    return await this.table.filter(req =>
      req.requisitionNumber.toLowerCase().includes(term) ||
      req.requestedBy.toLowerCase().includes(term) ||
      req.department.toLowerCase().includes(term)
    ).toArray();
  }
}

export const inventoryItemRepository = new InventoryItemRepository();
export const stockTransactionRepository = new StockTransactionRepository();
export const requisitionRepository = new RequisitionRepository();


